import {
  FormattedRelativeTimeOptions,
  FormattedRelativeTimeProps,
  NullableDateInput,
  parseDate,
  toPrimitiveDateInput,
  useDateConfig,
  useFormattedRelativeTime,
} from '@superdispatch/dates';
import { renderChildren } from '@superdispatch/ui';
import { useEffect, useMemo, useState } from 'react';

const HOUR_IN_SECONDS = 3600;
const MAX_TIMEOUT_MS = 2_147_483_647;

function useFormattedRelativeTimeToNow(
  input: NullableDateInput,
  { format, ...options }: Omit<FormattedRelativeTimeOptions, 'base'> = {},
): string {
  const [now, setNow] = useState(Date.now);
  const config = useDateConfig({ format });
  const primitiveInput = toPrimitiveDateInput(input);
  const date = useMemo(
    () => parseDate(primitiveInput, config),
    [config, primitiveInput],
  );

  useEffect(() => {
    const diffInSeconds = Math.max(
      // Do not update less then once per hour.
      HOUR_IN_SECONDS,
      Math.ceil(
        // Round up seconds.
        Math.abs(now - date.toMillis()) / 1000,
      ),
    );

    const timeoutID = setTimeout(
      () => {
        setNow(Date.now);
      },
      Math.min(diffInSeconds * 1000, MAX_TIMEOUT_MS),
    );

    return () => {
      clearTimeout(timeoutID);
    };
  }, [now, date]);

  return useFormattedRelativeTime(primitiveInput, {
    ...options,
    format,
    base: now,
  });
}

type FormattedRelativeTimeToNowProps = Omit<
  FormattedRelativeTimeProps,
  'compare'
>;

export function FormattedRelativeTimeToNow({
  date,
  fallback = 'Invalid Date',
  ...options
}: FormattedRelativeTimeToNowProps) {
  const formatted = useFormattedRelativeTimeToNow(date, {
    ...options,
    fallback: '',
  });

  return renderChildren(formatted || fallback);
}
